import { UpperCasePipe } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, input, model, OnDestroy, signal, untracked, viewChild, viewChildren } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { APP_CONFIG, dateFormat, DeviceEnum, PAGE, SearchItems } from '../../core';
import { ExtendedSearchItems, extendedSearchItemsMapper } from '../../models';
import { UserService } from '../../services';
import { BaseComponent, ConvertDatePipe, PlanStatusClassEnum, PlanStatusLabelPipe, PlanStatusPipe, ReplacePipe } from '../../shared';
import { PlanActionBoxComponent } from '../plan-action-box';
import { SortableHeaderComponent, SortEvent } from '../sortable-header';


/**
 * Nested plan table component.
 */
@Component({
	selector: 'app-nested-plan-table',
	templateUrl: './nested-plan-table.component.html',
	imports: [
		NgbTooltip,
		ReplacePipe,
		FormsModule,
		UpperCasePipe,
		PlanStatusPipe,
		ConvertDatePipe,
		PlanStatusLabelPipe,
		PlanActionBoxComponent,
		SortableHeaderComponent
	],
	changeDetection: ChangeDetectionStrategy.Eager
})
export class NestedPlanTableComponent extends BaseComponent implements AfterViewInit, OnDestroy {

	private readonly router = inject(Router);
	private readonly config = inject(APP_CONFIG);
	private readonly userSrv = inject(UserService);

	plans = input<ExtendedSearchItems[]>();
	selectedPlan = model<ExtendedSearchItems | null>(null);

	items = signal<ExtendedSearchItems[]>([]);
	readonly fields = PlanFields;
	readonly dateFormat = dateFormat();
	private readonly _DEFAULT_SORT: SortEvent = { column: this.fields.planModifiedDate, direction: 'desc' };
	sortOrder = signal<SortEvent>({ ...this._DEFAULT_SORT });
	activeColumn = computed(() => this.sortOrder().column);
	isActionBoxDisabled = computed(() => this.items()[0]?.patientId !== this.selectedPlan()?.patientId);

	private readonly _headers = viewChildren(SortableHeaderComponent);

	readonly deviceEnum = DeviceEnum;
	readonly statusClassEnum = PlanStatusClassEnum;

	scrollContainer = viewChild<ElementRef<HTMLDivElement>>('scrollContainer');
	hasScrollbar = signal(false);
	private resizeObserver?: ResizeObserver;

	isTlhexUser = toSignal(this.userSrv.isTlhex());

	private readonly init = effect(() => {
		if (this.plans()) {
			untracked(() => this.items.set(this.plans()?.map(item => extendedSearchItemsMapper(item, this.labels())) ?? []));
			this.sortOrder.set({ ...this._DEFAULT_SORT });
		}
	});

	private readonly sortList = effect(() => this._sortList());

	ngAfterViewInit(): void {
		const el = this.scrollContainer()?.nativeElement;
		if (!el) return;
		this.resizeObserver = new ResizeObserver(() => {
			this.checkScrollVisibility(el);
		});
		this.resizeObserver.observe(el);
		this.checkScrollVisibility(el);
	}

	private checkScrollVisibility(el: HTMLElement): void {
		const isVisible = el.scrollHeight > el.clientHeight;
		if (this.hasScrollbar() !== isVisible) {
			this.hasScrollbar.set(isVisible);
		}
	}

	/**
	* Update column sort order and sort list.
	*/
	onSort(order: SortEvent) {
		const newOrder = { ...order };
		if (order.direction === '') {
			newOrder.direction = order.column === this._DEFAULT_SORT.column ? 'asc' : this._DEFAULT_SORT.direction;
			newOrder.column = this._DEFAULT_SORT.column;
		}
		this.sortOrder.set(newOrder);
	}

	private _sortList(): void {
		for (const h of this._headers()) {
			h.reset();
		}
		this._headers().find(h => h.name() === this.sortOrder().column)?.direction.set(this.sortOrder().direction);
		const field = this.sortOrder().column as keyof SearchItems;
		const desc = this.sortOrder().direction === 'desc';
		untracked(() => this.items.set([...this.items()].sort((a, b) => this._compareFunction(a[field], b[field], desc) || this._compareFunction(a.planNumber, b.planNumber, false))));
	}

	private _compareFunction(a: any, b: any, desc: boolean): number {
		const aNull = a === null || a === undefined;
		const bNull = b === null || b === undefined;
		if (aNull && bNull) return 0;
		if (aNull) return desc ? 1 : -1;
		if (bNull) return desc ? -1 : 1;
		if (a > b) return desc ? -1 : 1;
		if (a < b) return desc ? 1 : -1;
		return 0;
	}

	/**
	 * Check if plan is editable.
	 */
	isEditable(plan: SearchItems): boolean {
		return this.isPlanVersionValid(plan) && !plan.caseIsPublished && !plan.caseIsRevoked;
	}

	isPlanVersionValid(plan: SearchItems): boolean {
		return !!plan?.caseVersion && plan.caseVersion >= this.config.appVersion;
	}

	/**
	* Navigate to plan edit page.
	*/
	editPlan(planGuid: string) {
		this.router.navigate([`/${PAGE.PLAN_EDIT}`], { queryParams: { planGuid: planGuid } });
	}


	ngOnDestroy(): void {
		this.resizeObserver?.disconnect();
	}

}

/**
* Fields for plan sorting.
*/
export enum PlanFields {
	planNumber = 'planNumber',
	displayDevice = 'displayDevice',
	displayPlanStep = 'displayPlanStep',
	displayPlanType = 'displayPlanType',
	displayScenario = 'displayScenario',
	displaySide = 'displaySide',
	displayBoneType = 'displayBoneType',
	planFrameId = 'frameID',
	planDaysLeft = 'caseDaysLeft',
	planModifiedDate = 'caseModifiedDate'
}
